import { PendingLiveConsentChange, PlatformTrackingConsentState } from './types';

/** OneTrust's "Targeting Cookies" category - the cookie group this preference reads as its ceiling. */
const TARGETING_COOKIES_GROUP_ID = 'C0004';

/** OneTrust's Sale of Personal Data group. The checkbox writes it with Targeting Cookies. */
const SALE_OF_PERSONAL_DATA_GROUP_ID = 'SPD_BG';

/**
 * OneTrust groups the Ads Personalization checkbox writes together.
 * SPD_BG follows the same on/off choice as Targeting Cookies.
 */
const INTEREST_BASED_ADVERTISING_GROUP_IDS = [
  TARGETING_COOKIES_GROUP_ID,
  SALE_OF_PERSONAL_DATA_GROUP_ID,
] as const;

type ConsentChangeListener = (state: PendingLiveConsentChange) => void;

const consentChangeListeners = new Set<ConsentChangeListener>();
let registeredOneTrust: Window['OneTrust'] | null = null;
let registrationTimer: ReturnType<typeof setTimeout> | undefined;
let registrationAttempts = 0;
const MAX_REGISTRATION_ATTEMPTS = 20;

const formatConsentGroups = (enabled: boolean): string =>
  INTEREST_BASED_ADVERTISING_GROUP_IDS.map(groupId => `${groupId}:${enabled ? 1 : 0}`).join(',');

/**
 * sessionStorage key used to hand a just-made, explicit OneTrust panel choice across the
 * full-page reload that public/oneTrust.js's OptanonWrapper triggers on any consent change
 * (see cleanDisallowedCookies/OnConsentChanged there). Without this, the next mount can't
 * tell "the user just changed this in the OneTrust panel" apart from "this browser's cookie
 * is stale relative to the account" - see docs/adr/0002-iba-cross-device-sync-without-a-timestamp.md.
 */
const PENDING_LIVE_CHANGE_STORAGE_KEY = 'rbi:iba:pendingLiveConsentChange';

/**
 * Set while the account checkbox writes consent. public/oneTrust.js reads it and skips the
 * reload it otherwise does on any consent change. OneTrust already flips the open toggle
 * and writes the cookie before that reload would run.
 */
const SKIP_CONSENT_RELOAD_STORAGE_KEY = 'rbi:iba:skipConsentReload';

/**
 * OneTrust can emit OnConsentChanged after UpdateConsent's promise resolves, and it can
 * emit more than once when both groups change. The flag stays set for that long, or those
 * callbacks reload the page before the checkbox handler decides to.
 */
const SKIP_CONSENT_RELOAD_MS = 2000;

/** How long an opt-out waits for OneTrust to report the groups off before reloading anyway. */
const CONSENT_WRITE_TIMEOUT_MS = 2000;
const CONSENT_WRITE_POLL_MS = 50;

/**
 * Set when this tab changes advertising consent. Sign-in copies a denial into the account.
 * A cookie that is already off, with no choice made in this tab, does not.
 * Must stay aligned with public/oneTrust.js.
 */
const SESSION_ADVERTISING_CHOICE_KEY = 'rbi:iba:sessionAdvertisingChoice';

/**
 * Set when the guest opts out. Cookie consent is written after Account Info's Update
 * finishes email and OTP confirmation, not when the checkbox changes.
 */
const PENDING_COOKIE_OPT_OUT_KEY = 'rbi:iba:pendingCookieOptOut';
const RETAINED_PENDING_COOKIE_OPT_OUT_OWNER_KEY = 'rbi:iba:retainedPendingCookieOptOutOwner';

/** Fired when that confirmed account update is ready for OneTrust. */
export const ACCOUNT_ADVERTISING_OPT_OUT_SAVED_EVENT = 'rbi:iba:accountOptOutSaved';

let ownAdvertisingConsentWrite = false;

/** True while this tab is writing OneTrust, so that write is not saved to the account again. */
export const isOwnAdvertisingConsentWrite = (): boolean => ownAdvertisingConsentWrite;

export const clearOwnAdvertisingConsentWrite = (): void => {
  ownAdvertisingConsentWrite = false;
};

type AccountOptOutSavedListener = () => Promise<void> | void;

const OPT_OUT_SAVED_LISTENERS = '__rbiIbaOptOutSavedListeners';

const optOutSavedListeners = (): Set<AccountOptOutSavedListener> => {
  const host = window as Window & {
    [OPT_OUT_SAVED_LISTENERS]?: Set<AccountOptOutSavedListener>;
  };
  if (!host[OPT_OUT_SAVED_LISTENERS]) {
    host[OPT_OUT_SAVED_LISTENERS] = new Set();
  }
  return host[OPT_OUT_SAVED_LISTENERS];
};

export const subscribeToAccountAdvertisingOptOutSaved = (
  onSaved: AccountOptOutSavedListener
): (() => void) => {
  if (typeof window === 'undefined') {
    return () => undefined;
  }

  const listeners = optOutSavedListeners();
  listeners.add(onSaved);
  return () => {
    listeners.delete(onSaved);
  };
};

export const markAdvertisingOptOutForAccountUpdate = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.sessionStorage?.removeItem(RETAINED_PENDING_COOKIE_OPT_OUT_OWNER_KEY);
    window.sessionStorage?.setItem(PENDING_COOKIE_OPT_OUT_KEY, '1');
  } catch {
    // Without this mark, the confirmed account update leaves the cookie groups on.
  }
};

export const clearAdvertisingOptOutForAccountUpdate = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.sessionStorage?.removeItem(RETAINED_PENDING_COOKIE_OPT_OUT_OWNER_KEY);
    window.sessionStorage?.removeItem(PENDING_COOKIE_OPT_OUT_KEY);
  } catch {
    // A leftover mark reloads on the next confirmed account update.
  }
};

/** Notifies opt-out listeners. Account Info dispatches the same event after email and OTP. */
export const publishAccountAdvertisingOptOutSaved = async (): Promise<void> => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.dispatchEvent?.(new CustomEvent(ACCOUNT_ADVERTISING_OPT_OUT_SAVED_EVENT));
  } catch {
    // The in-app listeners below still run when CustomEvent is unavailable.
  }

  await Promise.all(Array.from(optOutSavedListeners(), listener => listener()));
};

let guestTrackingConsentMerge: Promise<void> = Promise.resolve();

/** Mirrors public/oneTrust.js's parseActiveGroups(), typed for use inside the app bundle. */
const parseGroupStatuses = (): Map<string, boolean> => {
  if (typeof document === 'undefined') {
    return new Map();
  }

  let match: RegExpMatchArray | null = null;
  try {
    match = document.cookie.match(/(?:^|;\s*)OptanonConsent=([^;]+)/);
  } catch {
    return new Map();
  }
  if (!match) {
    return new Map();
  }

  const consentUri = decodeURIComponent(match[1]);
  const groupsMatch = consentUri.match(/groups=([^&]+)/);
  if (!groupsMatch) {
    return new Map();
  }

  const statuses = new Map<string, boolean>();
  groupsMatch[1].split(',').forEach(group => {
    const [groupId, status] = group.split(':');
    if (groupId) {
      statuses.set(groupId, status === '1');
    }
  });
  return statuses;
};

const parseActiveGroups = (): Set<string> => {
  const active = new Set<string>();
  parseGroupStatuses().forEach((isActive, groupId) => {
    if (isActive) {
      active.add(groupId);
    }
  });
  return active;
};

/**
 * OneTrust updates `OnetrustActiveGroups` before OnConsentChanged runs. The OptanonConsent
 * cookie can still hold the previous choice at that moment, so a live change has to read
 * the active-group string first.
 */
const readLiveActiveGroupIds = (): Set<string> | null => {
  const raw = window.OnetrustActiveGroups;
  if (typeof raw !== 'string' || raw.length === 0) {
    return null;
  }

  return new Set(
    raw
      .split(',')
      .map(group => group.trim())
      .filter(Boolean)
  );
};

const domainIncludesSaleOfPersonalData = (): boolean => {
  const groups = window.OneTrust?.GetDomainData?.()?.Groups;
  if (!Array.isArray(groups)) {
    return false;
  }

  return groups.some(
    group =>
      group?.CustomGroupId === SALE_OF_PERSONAL_DATA_GROUP_ID ||
      group?.OptanonGroupId === SALE_OF_PERSONAL_DATA_GROUP_ID
  );
};

/**
 * Advertising consent is on only when Targeting Cookies are on. SPD_BG off is off as well:
 * the checkbox writes both groups, and the visible US toggle is SPD_BG.
 * When OneTrust has not published active groups, an explicit SPD_BG:0 in the cookie is enough.
 * When the domain data includes SPD_BG and the group is inactive, that is off too.
 * If neither source mentions SPD_BG, Targeting Cookies alone decide.
 */
const readAdvertisingConsent = (): PendingLiveConsentChange => {
  const liveActive = readLiveActiveGroupIds();
  const active = liveActive ?? parseActiveGroups();
  if (!active.has(TARGETING_COOKIES_GROUP_ID)) {
    return 'denied';
  }

  if (!active.has(SALE_OF_PERSONAL_DATA_GROUP_ID)) {
    if (domainIncludesSaleOfPersonalData()) {
      return 'denied';
    }

    if (parseGroupStatuses().get(SALE_OF_PERSONAL_DATA_GROUP_ID) === false) {
      return 'denied';
    }
  }

  return 'granted';
};

const readLiveConsentChange = (): PendingLiveConsentChange => readAdvertisingConsent();

const recordSessionAdvertisingChoice = (state: PendingLiveConsentChange) => {
  try {
    window.sessionStorage?.setItem(SESSION_ADVERTISING_CHOICE_KEY, state);
  } catch {
    // Without this marker, sign-in leaves the account unchanged.
  }
};

const stashLiveConsentChange = (state: PendingLiveConsentChange) => {
  recordSessionAdvertisingChoice(state);
  try {
    window.sessionStorage?.setItem(PENDING_LIVE_CHANGE_STORAGE_KEY, state);
  } catch {
    // sessionStorage can throw in locked-down browser contexts; losing this marker just
    // means the next load falls back to treating the account as authoritative (rule 2).
  }
};

const notifyConsentChange = () => {
  const state = readLiveConsentChange();
  stashLiveConsentChange(state);
  consentChangeListeners.forEach(listener => listener(state));
};

const ensureOneTrustRegistration = () => {
  if (typeof window === 'undefined') {
    return;
  }

  const oneTrust = window.OneTrust;
  if (!oneTrust?.OnConsentChanged) {
    if (registrationTimer !== undefined || registrationAttempts >= MAX_REGISTRATION_ATTEMPTS) {
      return;
    }

    registrationAttempts += 1;
    registrationTimer = setTimeout(() => {
      registrationTimer = undefined;
      ensureOneTrustRegistration();
    }, 500);
    return;
  }

  registrationAttempts = 0;
  if (registeredOneTrust === oneTrust) {
    return;
  }

  registeredOneTrust = oneTrust;
  oneTrust.OnConsentChanged(() => {
    notifyConsentChange();
  });
};

export const getPlatformTrackingConsentState = async (): Promise<PlatformTrackingConsentState> => {
  if (typeof document === 'undefined' || !document.cookie.includes('OptanonConsent=')) {
    // OneTrust hasn't recorded a choice for this browser yet.
    return 'unknown';
  }

  return readAdvertisingConsent();
};

/** Drops a denial recorded in this tab so a later profile opt-in is not written back to opted out. */
export const clearSameSessionAdvertisingDenial = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    if (window.sessionStorage?.getItem(SESSION_ADVERTISING_CHOICE_KEY) === 'denied') {
      window.sessionStorage.removeItem(SESSION_ADVERTISING_CHOICE_KEY);
    }
  } catch {
    // A leftover denial opts the account back out the next time Account Info opens.
  }
};

let retainPendingCookieOptOut = false;
let retainedPendingCookieOptOutOwner: string | null = null;

/**
 * Account Info signs the current guest out before email and OTP, then signs the same guest
 * back in. That logout must keep the cookie opt-out waiting for the confirmed profile update.
 */
export const retainPendingCookieOptOutAcrossReauthentication = (): void => {
  retainPendingCookieOptOut = true;
};

export const releasePendingCookieOptOutRetention = (): void => {
  retainPendingCookieOptOut = false;
  retainedPendingCookieOptOutOwner = null;
};

export const bindPendingCookieOptOutRetentionOwner = (owner: string | null): void => {
  retainedPendingCookieOptOutOwner = owner;
};

const shouldRetainPendingCookieOptOut = (context?: {
  reason?: string;
  previousScope?: string;
  nextScope?: string;
}) =>
  retainPendingCookieOptOut &&
  !!retainedPendingCookieOptOutOwner &&
  context?.reason === 'logout' &&
  context.previousScope === 'authenticated' &&
  context.nextScope === 'guest';

/**
 * Authentication transitions must retire authenticated-user-scoped advertising markers so a
 * previous user's choice is never applied to the next authenticated identity in the same tab.
 * The pending cookie opt-out survives only the re-authentication logout that Account Info
 * performs before email and OTP.
 */
export const clearAuthenticatedAdvertisingConsentState = (context?: {
  reason?: string;
  previousScope?: string;
  nextScope?: string;
}): void => {
  const retainPendingOptOut = shouldRetainPendingCookieOptOut(context);
  const retainedOwner = retainedPendingCookieOptOutOwner;
  retainPendingCookieOptOut = false;
  retainedPendingCookieOptOutOwner = null;
  bannerConsentHandoff = null;
  ownAdvertisingConsentWrite = false;

  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.sessionStorage?.removeItem(SESSION_ADVERTISING_CHOICE_KEY);
    window.sessionStorage?.removeItem(PENDING_LIVE_CHANGE_STORAGE_KEY);
    window.sessionStorage?.removeItem(SKIP_CONSENT_RELOAD_STORAGE_KEY);
    if (!retainPendingOptOut || !retainedOwner) {
      window.sessionStorage?.removeItem(RETAINED_PENDING_COOKIE_OPT_OUT_OWNER_KEY);
      window.sessionStorage?.removeItem(PENDING_COOKIE_OPT_OUT_KEY);
      return;
    }
    window.sessionStorage?.setItem(RETAINED_PENDING_COOKIE_OPT_OUT_OWNER_KEY, retainedOwner);
  } catch {
    // A stale marker can leak one user's state to the next user in this tab.
  }
};

export const reconcileRetainedPendingCookieOptOutOnSignIn = (
  authenticatedOwner: string | null
): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const retainedOwner = window.sessionStorage?.getItem(RETAINED_PENDING_COOKIE_OPT_OUT_OWNER_KEY);
    if (!retainedOwner) {
      return;
    }

    const shouldKeepPending = authenticatedOwner !== null && retainedOwner === authenticatedOwner;
    if (!shouldKeepPending) {
      window.sessionStorage?.removeItem(PENDING_COOKIE_OPT_OUT_KEY);
    }
    window.sessionStorage?.removeItem(RETAINED_PENDING_COOKIE_OPT_OUT_OWNER_KEY);
  } catch {
    // Fail open: a later authenticated cleanup still removes stale markers.
  }
};

/** True when this tab turned advertising consent off. A stale cookie on another browser is not. */
export const hasSameSessionAdvertisingDenial = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    return window.sessionStorage?.getItem(SESSION_ADVERTISING_CHOICE_KEY) === 'denied';
  } catch {
    return false;
  }
};

/**
 * Sign-in starts this before Account Info can mount. The preference hook waits on it so a
 * cold load cannot write the previous account value back onto a same-session denial.
 */
export const trackGuestTrackingConsentMerge = (pending: Promise<void>): void => {
  guestTrackingConsentMerge = pending.then(
    () => undefined,
    () => undefined
  );
};

export const waitForGuestTrackingConsentMerge = (): Promise<void> => guestTrackingConsentMerge;

/**
 * A denial made in this tab, with Targeting Cookies or SPD_BG off, opts the account out.
 * This does not turn groups back on. A denied cookie with no same-session choice is left
 * for the account to correct after sign-in.
 */
export const applyGuestAdvertisingOptOutOnSignIn = async (
  persistOptOut: () => Promise<void>
): Promise<void> => {
  if (!hasSameSessionAdvertisingDenial()) {
    return;
  }

  const state = await getPlatformTrackingConsentState();
  if (state === 'denied') {
    await persistOptOut();
  }
};

// Web has no separate "request" step distinct from reading the current cookie - there's no
// system permission prompt to trigger here, OneTrust's own cookie IS the consent record.
export const requestPlatformTrackingConsent = getPlatformTrackingConsentState;

/**
 * Blocks `location.reload` and tells public/oneTrust.js to skip its own reload until
 * `release(shouldReload)` runs. OneTrust reloads inside UpdateConsent, which can cancel
 * the account update that is still in flight. Call this before that update starts.
 */
export const deferPageReload = (): ((shouldReload: boolean) => void) => {
  if (typeof window === 'undefined') {
    return () => undefined;
  }

  const location = window.location;
  const originalReload = location.reload?.bind(location);
  let released = false;
  let restoreReload = () => undefined;

  try {
    location.reload = () => undefined;
    restoreReload = () => {
      location.reload = originalReload;
    };
  } catch {
    const prototype = Object.getPrototypeOf(location) as { reload?: () => void };
    const originalPrototypeReload = prototype.reload;
    if (originalPrototypeReload) {
      prototype.reload = () => undefined;
      restoreReload = () => {
        prototype.reload = originalPrototypeReload;
      };
    }
  }

  try {
    window.sessionStorage?.setItem(SKIP_CONSENT_RELOAD_STORAGE_KEY, '1');
  } catch {
    // If this flag cannot be stored, the banner listener reloads the page as usual.
  }

  return (shouldReload: boolean) => {
    if (released) {
      return;
    }
    released = true;
    restoreReload();

    if (!shouldReload) {
      setTimeout(() => {
        try {
          window.sessionStorage?.removeItem(SKIP_CONSENT_RELOAD_STORAGE_KEY);
        } catch {
          // The listener treats a missing flag as "reload", which is the banner path.
        }
      }, SKIP_CONSENT_RELOAD_MS);
      return;
    }

    try {
      window.sessionStorage?.removeItem(SKIP_CONSENT_RELOAD_STORAGE_KEY);
    } catch {
      // If this fails, the page still reloads and the banner listener falls back to default behavior.
    }
    originalReload?.();
  };
};

/**
 * Resolves when OneTrust finishes applying the consent write, or after a timeout.
 * OnConsentChanged runs before that write, and reloading there drops the update.
 */
export const waitForOneTrustConsentApplied = (): Promise<void> => {
  if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') {
    return Promise.resolve();
  }

  return new Promise(resolve => {
    const finish = () => {
      clearTimeout(timeout);
      window.removeEventListener('OTConsentApplied', onApplied);
      resolve();
    };
    const onApplied = () => finish();
    const timeout = setTimeout(finish, CONSENT_WRITE_TIMEOUT_MS);
    window.addEventListener('OTConsentApplied', onApplied);
  });
};

export const setPlatformTrackingConsent = async (enabled: boolean): Promise<boolean> => {
  if (typeof window === 'undefined' || !window.OneTrust?.UpdateConsent) {
    return false;
  }

  ownAdvertisingConsentWrite = true;

  // OneTrust applies these groups only when the interaction type is "Category".
  // Any other value, including "api", leaves the preference-center toggle unchanged.
  recordSessionAdvertisingChoice(enabled ? 'granted' : 'denied');

  try {
    window.sessionStorage?.setItem(SKIP_CONSENT_RELOAD_STORAGE_KEY, '1');
  } catch {
    // If this flag cannot be stored, the banner listener reloads the page as usual.
  }

  try {
    await window.OneTrust.UpdateConsent('Category', formatConsentGroups(enabled));
  } finally {
    // Clear after late OnConsentChanged callbacks. Clearing in this turn lets those
    // callbacks reload an opt-in.
    setTimeout(() => {
      ownAdvertisingConsentWrite = false;
      try {
        window.sessionStorage?.removeItem(SKIP_CONSENT_RELOAD_STORAGE_KEY);
      } catch {
        // The listener treats a missing flag as "reload", which is the banner path.
      }
    }, SKIP_CONSENT_RELOAD_MS);
  }

  return true;
};

/**
 * Writes the opt-out into OneTrust and reloads. Account Info calls this after email and
 * OTP confirmation and the profile update have both succeeded.
 */
export const applyAdvertisingOptOutAfterAccountUpdate = async (): Promise<boolean> => {
  if (typeof window === 'undefined') {
    return false;
  }

  let pending = false;
  try {
    pending = window.sessionStorage?.getItem(PENDING_COOKIE_OPT_OUT_KEY) === '1';
  } catch {
    return false;
  }
  if (!pending) {
    return false;
  }

  const finishReloadHold = deferPageReload();
  let reload = false;

  try {
    try {
      window.dispatchEvent?.(new CustomEvent(ACCOUNT_ADVERTISING_OPT_OUT_SAVED_EVENT));
    } catch {
      // Consent is still written below when CustomEvent is unavailable.
    }

    const consentApplied = waitForOneTrustConsentApplied();
    const wroteConsent = await setPlatformTrackingConsent(false);
    if (!wroteConsent) {
      throw new Error('OneTrust consent API unavailable');
    }
    await waitForPlatformTrackingConsentState('denied');
    await consentApplied;
    if (readAdvertisingConsent() !== 'denied') {
      throw new Error('Advertising consent write did not apply');
    }
    reload = true;
    window.sessionStorage?.removeItem(RETAINED_PENDING_COOKIE_OPT_OUT_OWNER_KEY);
    window.sessionStorage?.removeItem(PENDING_COOKIE_OPT_OUT_KEY);
  } catch {
    // Keep the pending mark so the next confirmed account update can retry the cookie write.
  } finally {
    finishReloadHold(reload);
    if (reload) {
      ownAdvertisingConsentWrite = false;
    }
  }

  return reload;
};

/**
 * Resolves once OneTrust reports the advertising groups in the given state, or after a
 * timeout. UpdateConsent can resolve before OneTrust has written the cookie, and a reload
 * at that moment brings the old toggle back.
 */
export const waitForPlatformTrackingConsentState = async (
  state: PendingLiveConsentChange
): Promise<void> => {
  if (typeof window === 'undefined') {
    return;
  }

  const deadline = Date.now() + CONSENT_WRITE_TIMEOUT_MS;
  while (readAdvertisingConsent() !== state && Date.now() < deadline) {
    await new Promise(resolve => setTimeout(resolve, CONSENT_WRITE_POLL_MS));
  }
};

/**
 * Web always has write access via OneTrust.UpdateConsent (confirmed available - see
 * docs/adr/0001-interest-based-advertising-is-a-ceiling-gated-opt-out.md), so unlike iOS,
 * there is no "denied and can't be changed from here" case that needs a Settings redirect.
 */
export const tryEnablePlatformTrackingConsent = async (): Promise<boolean> => {
  return setPlatformTrackingConsent(true);
};

/**
 * Registers a listener for OneTrust consent changes.
 * The callback runs when the page stays up. public/oneTrust.js also writes the same
 * sessionStorage marker before it reloads, and the next mount applies that marker to the account.
 */
export const subscribeToPlatformTrackingConsentChange = (
  onChange?: ConsentChangeListener
): (() => void) => {
  if (onChange) {
    consentChangeListeners.add(onChange);
  }

  ensureOneTrustRegistration();

  return () => {
    if (onChange) {
      consentChangeListeners.delete(onChange);
    }
  };
};

export const notifyIosTrackingDenialPersistenceResult = (saved: boolean): void => {
  void saved;
};

export const subscribeToIosTrackingDenialPersistenceResult = (
  onResult?: (saved: boolean) => void
): (() => void) => {
  void onResult;
  return () => undefined;
};

// Set when a banner change is taken out of sessionStorage. Account Info claims it so a
// save started elsewhere on this load does not also rewrite cookies from the old account value.
let bannerConsentHandoff: PendingLiveConsentChange | null = null;

export const consumePendingLiveConsentChange = (): PendingLiveConsentChange | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const value = window.sessionStorage?.getItem(PENDING_LIVE_CHANGE_STORAGE_KEY);
    if (value === 'granted' || value === 'denied') {
      window.sessionStorage.removeItem(PENDING_LIVE_CHANGE_STORAGE_KEY);
      bannerConsentHandoff = value;
      return value;
    }
  } catch {
    // ignore - treated the same as "no pending change".
  }

  return null;
};

export const claimBannerConsentHandoff = (): PendingLiveConsentChange | null => {
  const value = bannerConsentHandoff;
  bannerConsentHandoff = null;
  return value;
};

/**
 * A banner opt-out is written to the account. A banner opt-in is not: the account stays as it
 * was. Returns true whenever a banner change was waiting, so the caller does not write the old
 * account value back onto the cookies that just changed.
 */
export const applyPendingLiveAdvertisingConsent = async (
  accountEnabled: boolean,
  persist: (enabled: boolean) => Promise<unknown>
): Promise<boolean> => {
  const pending = consumePendingLiveConsentChange();
  if (!pending) {
    return false;
  }

  if (pending === 'denied' && accountEnabled) {
    try {
      const persisted = await persist(false);
      if (persisted === false) {
        stashLiveConsentChange(pending);
      }
    } catch {
      stashLiveConsentChange(pending);
    }
  }

  return true;
};

export const notifyPlatformTrackingConsentChange = (_state: PendingLiveConsentChange): void => {};

export const applyIosTrackingDenial = async (
  _accountEnabled: boolean,
  _persistOptOut: () => Promise<void>
): Promise<void> => {};

/**
 * An authenticated guest whose cookie consent is opted out in this tab gets that opt-out
 * written through the user service. A banner opt-in is not written. Guests with no session
 * are skipped by the caller.
 */
export const applyAuthenticatedAdvertisingOptOut = async (
  accountEnabled: boolean,
  persistOptOut: () => Promise<void>
): Promise<void> => {
  if (!accountEnabled) {
    const appliedPending = await applyPendingLiveAdvertisingConsent(false, async () => undefined);
    if (appliedPending) {
      claimBannerConsentHandoff();
    }
    return;
  }

  const appliedPending = await applyPendingLiveAdvertisingConsent(true, async () => {
    await persistOptOut();
  });
  if (appliedPending) {
    claimBannerConsentHandoff();
    return;
  }

  if (!hasSameSessionAdvertisingDenial()) {
    return;
  }

  const state = await getPlatformTrackingConsentState();
  if (state === 'denied') {
    await persistOptOut();
  }
};

// Single source of truth for the window.OneTrust ambient type - see also
// src/pages/account/ccpa-button-link/index.web.tsx and src/pages/campaigns/campaigns.tsx,
// which call window.OneTrust.ToggleInfoDisplay() and rely on this same global declaration.
declare global {
  interface Window {
    OnetrustActiveGroups?: string;
    OneTrust: {
      ToggleInfoDisplay: () => void;
      UpdateConsent?: (interactionType: string, groupIds: string) => void | Promise<void>;
      OnConsentChanged?: (callback: () => void) => void;
      GetDomainData?: () => {
        Groups?: Array<{ CustomGroupId?: string; OptanonGroupId?: string }>;
      };
    };
  }
}
